import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import AppSidebarV2 from "../components/layout/AppSidebarV2";
import { useAuth } from "../auth/authStore";
import { psychologistAccessApi } from "../api/psychologistAccessApi";
import { subscriptionsApi } from "../api/subscriptionsApi";
import { useToast } from "../ui/toast/ToastProvider";
import { assignExternalUrl } from "../utils/safeUrl";

const BILLING_MONTH = "month";
const BILLING_YEAR = "year";
const PENDING_SUB_CHECKOUT_KEY = "bs:subscription:pending-checkout";

function isActiveSubscriptionStatus(raw) {
  const token = String(raw || "").trim().toUpperCase();
  return token.includes("ACTIVE") || token.includes("PAID") || token.includes("SUCCESS");
}

function hasPositivePaymentToken(raw) {
  const token = String(raw || "").trim().toLowerCase();
  return ["success", "succeeded", "paid", "ok"].includes(token);
}

function hasNegativePaymentToken(raw) {
  const token = String(raw || "").trim().toLowerCase();
  return ["cancel", "cancelled", "canceled", "failed", "error"].includes(token);
}

function readPendingSubscriptionCheckout() {
  if (typeof window === "undefined") return null;
  try {
    const parsed = JSON.parse(window.sessionStorage.getItem(PENDING_SUB_CHECKOUT_KEY) || "null");
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch {
    return null;
  }
}

function writePendingSubscriptionCheckout(payload) {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(PENDING_SUB_CHECKOUT_KEY, JSON.stringify(payload || {}));
  } catch {
    // ignore
  }
}

function clearPendingSubscriptionCheckout() {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(PENDING_SUB_CHECKOUT_KEY);
  } catch {
    // ignore
  }
}

function SubscriptionIcon({ name = "shield-check", className = "", strokeWidth = 1.8 }) {
  const common = {
    className: `subscription-icon subscription-icon--${name} ${className}`.trim(),
    viewBox: "0 0 24 24",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    "aria-hidden": "true",
  };

  const strokeProps = {
    stroke: "currentColor",
    strokeWidth,
    strokeLinecap: "round",
    strokeLinejoin: "round",
  };

  switch (name) {
    case "shield-check":
      return (
        <svg {...common}>
          <path {...strokeProps} d="M12 3.2 18.5 5.9v5.7c0 4.5-2.55 7.9-6.5 9.2-3.95-1.3-6.5-4.7-6.5-9.2V5.9L12 3.2Z" />
          <path {...strokeProps} d="m9.2 12.1 1.9 1.9 4-4.3" />
        </svg>
      );

    case "analytics":
      return (
        <svg {...common}>
          <path {...strokeProps} d="M4.5 19.5h15" />
          <path {...strokeProps} d="M7 16.5V11" />
          <path {...strokeProps} d="M12 16.5V7" />
          <path {...strokeProps} d="M17 16.5V4.8" />
        </svg>
      );

    case "scale":
      return (
        <svg {...common}>
          <path {...strokeProps} d="M7 8.5c2.6-3 7.4 4 10 1.1" />
          <path {...strokeProps} d="M17 15.5c-2.6 3-7.4-4-10-1.1" />
          <path {...strokeProps} d="M7 8.5c-2.15 0-3.5 1.6-3.5 3.5S4.85 15.5 7 15.5" />
          <path {...strokeProps} d="M17 8.5c2.15 0 3.5 1.6 3.5 3.5s-1.35 3.5-3.5 3.5" />
        </svg>
      );

    case "workspace":
      return (
        <svg {...common}>
          <path {...strokeProps} d="M5 6.5h14a1.5 1.5 0 0 1 1.5 1.5v9.5A1.5 1.5 0 0 1 19 19H5a1.5 1.5 0 0 1-1.5-1.5V8A1.5 1.5 0 0 1 5 6.5Z" />
          <path {...strokeProps} d="M8 6.5V5.2A1.2 1.2 0 0 1 9.2 4h5.6A1.2 1.2 0 0 1 16 5.2v1.3" />
          <path {...strokeProps} d="M3.5 11.2h17" />
          <path {...strokeProps} d="M10 12.2h4" />
        </svg>
      );

    case "check":
      return (
        <svg {...common}>
          <circle {...strokeProps} cx="12" cy="12" r="8" />
          <path {...strokeProps} d="m8.8 12.2 2.05 2.05L15.4 9.7" />
        </svg>
      );

    case "x":
      return (
        <svg {...common}>
          <circle {...strokeProps} cx="12" cy="12" r="8" />
          <path {...strokeProps} d="m9.4 9.4 5.2 5.2" />
          <path {...strokeProps} d="m14.6 9.4-5.2 5.2" />
        </svg>
      );

    case "card":
      return (
        <svg {...common}>
          <rect {...strokeProps} x="3.5" y="6" width="17" height="12" rx="2" />
          <path {...strokeProps} d="M3.5 10h17" />
          <path {...strokeProps} d="M7 14.5h4.5" />
        </svg>
      );

    case "bank":
      return (
        <svg {...common}>
          <path {...strokeProps} d="M4 9.5h16" />
          <path {...strokeProps} d="M5.5 18.5h13" />
          <path {...strokeProps} d="M6.5 9.5v9" />
          <path {...strokeProps} d="M10.2 9.5v9" />
          <path {...strokeProps} d="M13.8 9.5v9" />
          <path {...strokeProps} d="M17.5 9.5v9" />
          <path {...strokeProps} d="M12 4.5 20 8.7H4l8-4.2Z" />
        </svg>
      );

    case "lock":
      return (
        <svg {...common}>
          <rect {...strokeProps} x="5.5" y="10" width="13" height="10" rx="2" />
          <path {...strokeProps} d="M8.5 10V7.6A3.5 3.5 0 0 1 12 4.1a3.5 3.5 0 0 1 3.5 3.5V10" />
          <path {...strokeProps} d="M12 14v2.2" />
        </svg>
      );

    case "refresh":
      return (
        <svg {...common}>
          <path {...strokeProps} d="M18.4 8.1A7 7 0 0 0 6 7.4L4.6 9.2" />
          <path {...strokeProps} d="M4.6 5.4v3.8h3.8" />
          <path {...strokeProps} d="M5.6 15.9A7 7 0 0 0 18 16.6l1.4-1.8" />
          <path {...strokeProps} d="M19.4 18.6v-3.8h-3.8" />
        </svg>
      );

    case "calendar":
      return (
        <svg {...common}>
          <rect {...strokeProps} x="4" y="5.5" width="16" height="14" rx="2" />
          <path {...strokeProps} d="M8 3.8v3.4" />
          <path {...strokeProps} d="M16 3.8v3.4" />
          <path {...strokeProps} d="M4 10h16" />
          <path {...strokeProps} d="M8 14h2.2" />
          <path {...strokeProps} d="M13.8 14H16" />
        </svg>
      );

    case "document":
      return (
        <svg {...common}>
          <path {...strokeProps} d="M7 3.8h6.8L18 8v12.2H7V3.8Z" />
          <path {...strokeProps} d="M13.5 3.9V8.3h4.3" />
          <path {...strokeProps} d="M9.6 12.4h4.8" />
          <path {...strokeProps} d="M9.6 15.5h4.8" />
        </svg>
      );

    default:
      return (
        <svg {...common}>
          <circle {...strokeProps} cx="12" cy="12" r="8" />
        </svg>
      );
  }
}

function PlanShieldIcon() {
  return <SubscriptionIcon name="shield-check" className="subscription-current-plan__icon-svg" strokeWidth={1.65} />;
}

function FeatureStateIcon({ enabled = true }) {
  return <SubscriptionIcon name={enabled ? "check" : "x"} strokeWidth={2} />;
}

function normalizeRoleToken(raw) {
  return String(raw || "")
    .replace(/^ROLE_/, "")
    .trim()
    .toUpperCase();
}

function displayRole(raw) {
  const role = normalizeRoleToken(raw);
  if (role === "ADMIN") return "Администратор";
  if (role === "PSYCHOLOGIST") return "Психолог";
  if (role === "MODERATOR") return "Модератор";
  if (role === "CLIENT") return "Клиент";
  return "Пользователь";
}

function formatMoney(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "0 ₽";
  return `${new Intl.NumberFormat("ru-RU").format(Math.max(0, amount))} ₽`;
}

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("ru-RU", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  }).format(date);
}

function getErrorMessage(err, fallback = "Не удалось выполнить запрос.") {
  if (!err) return fallback;

  const code = String(err?.code || "").trim();
  const status = Number(err?.status ?? err?.response?.status ?? 0) || 0;
  const message =
    String(err?.message || "").trim() ||
    String(err?.error || "").trim() ||
    String(err?.detail || "").trim();

  if (code === "SUBSCRIPTION_ALREADY_ACTIVE") return "Этот тариф уже активен.";
  if (code === "SUBSCRIPTION_PLAN_REQUIRED") return "Выберите тариф для продолжения.";
  if (code === "SUBSCRIPTION_PAYMENT_REQUIRED") return "Не удалось инициировать оплату. Попробуйте еще раз.";
  if (code === "DISCOUNT_CODE_NOT_FOUND") return "Код не найден.";
  if (code === "DISCOUNT_CODE_INACTIVE") return "Код неактивен.";
  if (code === "DISCOUNT_CODE_EXPIRED") return "Срок действия кода истек.";
  if (code === "DISCOUNT_CODE_NOT_STARTED") return "Код пока не активен.";
  if (code === "DISCOUNT_CODE_LIMIT_REACHED") return "Лимит активаций кода исчерпан.";
  if (code === "DISCOUNT_CODE_USER_LIMIT_REACHED") return "Для вашего аккаунта лимит использования кода исчерпан.";
  if (code === "DISCOUNT_CODE_SCOPE_MISMATCH") return "Этот код не подходит для выбранного тарифа.";
  if (code === "DISCOUNT_CODE_PLAN_MISMATCH") return "Этот код не действует на выбранный тариф.";
  if (code === "DISCOUNT_CODE_MIN_ORDER_NOT_REACHED") return "Сумма тарифа меньше минимальной для этого кода.";
  if (code === "CERTIFICATE_NOT_FOUND") return "Сертификат не найден.";
  if (code === "CERTIFICATE_INACTIVE") return "Сертификат неактивен.";
  if (code === "CERTIFICATE_EXPIRED") return "Срок действия сертификата истек.";
  if (code === "CERTIFICATE_BALANCE_EMPTY") return "Баланс сертификата исчерпан.";
  if (code === "DISCOUNT_CODE_INVALID") return "Код не найден или недействителен.";

  if (status === 404) return "Код не найден или недействителен.";
  if (/^not found$/i.test(message)) return "Код не найден или недействителен.";

  if (message) return message;
  return fallback;
}

function resolveDefaultPlanCode(plans = [], currentPlanCode = "") {
  const all = Array.isArray(plans) ? plans : [];
  const current = String(currentPlanCode || "").trim().toUpperCase();
  if (current && all.some((plan) => String(plan?.code || "").trim().toUpperCase() === current)) {
    return current;
  }

  const popularPaid = all.find((plan) => Boolean(plan?.isPopular) && Number(plan?.monthlyPrice) > 0);
  if (popularPaid?.code) return String(popularPaid.code).trim().toUpperCase();

  const firstPaid = all.find((plan) => Number(plan?.monthlyPrice) > 0);
  if (firstPaid?.code) return String(firstPaid.code).trim().toUpperCase();

  const first = all[0];
  return String(first?.code || "FREE").trim().toUpperCase();
}

function planByCode(plans = [], code = "") {
  const token = String(code || "").trim().toUpperCase();
  return plans.find((plan) => String(plan?.code || "").trim().toUpperCase() === token) || null;
}

function statusTone(state) {
  const token = String(state?.status || "").trim().toUpperCase();
  if (state?.trialActive) return "trial";
  if (token.includes("ACTIVE") || token.includes("PAID") || token.includes("SUCCESS")) return "active";
  if (token.includes("CANCEL") || token.includes("EXPIRE")) return "danger";
  return "neutral";
}

function statusLabel(state) {
  const token = String(state?.status || "").trim().toUpperCase();
  if (state?.trialActive) return "Пробный период";
  if (token.includes("ACTIVE") || token.includes("PAID") || token.includes("SUCCESS")) return "Активна";
  if (token.includes("CANCEL")) return "Отменена";
  if (token.includes("EXPIRE")) return "Истекла";
  if (token.includes("PENDING")) return "Ожидает оплаты";
  return "Статус уточняется";
}

function normalizeVerificationState(raw) {
  return raw && typeof raw === "object" ? raw : {};
}

function verificationStatusTone(item) {
  const token = String(item?.status || "").trim().toUpperCase();
  if (token === "APPROVED") return "approved";
  if (token === "REJECTED") return "rejected";
  if (token === "PENDING") return "pending";
  return "draft";
}

function verificationStatusLabel(item) {
  const token = String(item?.status || "").trim().toUpperCase();
  if (token === "APPROVED") return "Заявка одобрена";
  if (token === "REJECTED") return "Заявка отклонена";
  if (token === "PENDING") return "На рассмотрении";
  return "Заявка не отправлена";
}

function verificationBadgeLabel(item) {
  const token = String(item?.status || "").trim().toUpperCase();
  if (token === "APPROVED") return "Одобрено";
  if (token === "REJECTED") return "Нужна доработка";
  if (token === "PENDING") return "На рассмотрении";
  return "Можно отправить заявку";
}

function getPlanPrice(plan, billingMode) {
  const monthly = Number(plan?.monthlyPrice) || 0;
  const yearlyTotalRaw = Number(plan?.yearlyTotal);
  const yearlyTotal = Number.isFinite(yearlyTotalRaw)
    ? Math.max(0, yearlyTotalRaw)
    : monthly > 0
      ? monthly * 12
      : 0;

  if (billingMode === BILLING_YEAR) {
    const yearlyMonthly = yearlyTotal > 0 ? Math.round(yearlyTotal / 12) : 0;
    return {
      amount: yearlyMonthly,
      period: "/мес",
      note: yearlyTotal > 0 ? `${formatMoney(yearlyTotal)}/год` : "",
      yearlyTotal,
      monthly,
    };
  }

  return {
    amount: monthly,
    period: "/мес",
    note: yearlyTotal > 0 && monthly > 0 ? `или ${formatMoney(yearlyTotal)}/год` : "",
    yearlyTotal,
    monthly,
  };
}

export default function Subscription() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { me, role, boot } = useAuth();
  const toast = useToast();
  const roleToken = normalizeRoleToken(role);
  const isClientRole = roleToken === "CLIENT";

  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState("");
  const [plans, setPlans] = React.useState([]);
  const [subscriptionState, setSubscriptionState] = React.useState(null);
  const [verificationState, setVerificationState] = React.useState(null);
  const [verificationNote, setVerificationNote] = React.useState("");
  const [submittingVerification, setSubmittingVerification] = React.useState(false);

  const [billingMode, setBillingMode] = React.useState(BILLING_MONTH);
  const [checkoutOpen, setCheckoutOpen] = React.useState(false);
  const [checkoutBillingMode, setCheckoutBillingMode] = React.useState(BILLING_MONTH);
  const [checkoutPlanCode, setCheckoutPlanCode] = React.useState("");
  const [paymentMethod, setPaymentMethod] = React.useState("card");
  const [checkoutCodeType, setCheckoutCodeType] = React.useState("promo");
  const [checkoutCodeValue, setCheckoutCodeValue] = React.useState("");
  const [checkoutDiscountPreview, setCheckoutDiscountPreview] = React.useState(null);
  const [checkingDiscountPreview, setCheckingDiscountPreview] = React.useState(false);
  const [checkoutCodeError, setCheckoutCodeError] = React.useState("");

  const [creatingPayment, setCreatingPayment] = React.useState(false);
  const [cancellingSubscription, setCancellingSubscription] = React.useState(false);

  const plansSectionRef = React.useRef(null);
  const lastHandledPaymentSignalRef = React.useRef("");

  const canOpenLiteratureEditor =
    roleToken === "ADMIN" ||
    roleToken === "PSYCHOLOGIST" ||
    roleToken === "MODERATOR";

  const loadSubscriptionData = React.useCallback(
    async ({ silent = false } = {}) => {
      if (!silent) setLoading(true);
      setLoadError("");

      try {
        const nextPlans = await subscriptionsApi.listPlans();
        const nextState = await subscriptionsApi.getMySubscription({ plans: nextPlans });

        setPlans(nextPlans);
        setSubscriptionState(nextState);

        setBillingMode(nextState?.billingMode || BILLING_MONTH);
        setCheckoutBillingMode(nextState?.billingMode || BILLING_MONTH);
        setCheckoutPlanCode((prev) => {
          const token = String(prev || "").trim().toUpperCase();
          if (token && planByCode(nextPlans, token)) return token;
          return resolveDefaultPlanCode(nextPlans, nextState?.planCode);
        });
        return nextState;
      } catch (err) {
        const message = getErrorMessage(err, "Не удалось загрузить данные подписки.");
        setLoadError(message);
        return null;
      } finally {
        if (!silent) setLoading(false);
      }
    },
    []
  );

  const loadVerificationData = React.useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setLoadError("");

    try {
      const nextState = normalizeVerificationState(await psychologistAccessApi.getMyRequest());
      setVerificationState(nextState);
      return nextState;
    } catch (err) {
      const message = getErrorMessage(err, "Не удалось загрузить статус заявки.");
      setLoadError(message);
      return null;
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (isClientRole) {
      loadVerificationData();
      return;
    }
    loadSubscriptionData();
  }, [isClientRole, loadSubscriptionData, loadVerificationData]);

  React.useEffect(() => {
    if (isClientRole) return;
    const paymentStatus = String(searchParams.get("payment") || "").trim().toLowerCase();
    const providerStatus = String(searchParams.get("status") || "").trim().toLowerCase();
    const paymentId = String(searchParams.get("paymentId") || "").trim();
    const paymentSignal = `${paymentStatus}:${providerStatus}:${paymentId}`;
    const hasPaymentSignal = Boolean(paymentStatus || providerStatus || paymentId);

    if (!hasPaymentSignal) return;
    if (lastHandledPaymentSignalRef.current === paymentSignal) return;

    lastHandledPaymentSignalRef.current = paymentSignal;
    const pending = readPendingSubscriptionCheckout();
    const negative = hasNegativePaymentToken(paymentStatus) || hasNegativePaymentToken(providerStatus);
    const positive = hasPositivePaymentToken(paymentStatus) || hasPositivePaymentToken(providerStatus);
    const isReturnFlow = paymentStatus === "return";

    const clearQuery = () => {
      const next = new URLSearchParams(searchParams);
      next.delete("payment");
      next.delete("paymentId");
      next.delete("provider");
      next.delete("status");
      setSearchParams(next, { replace: true });
    };

    (async () => {
      if (negative) {
        clearPendingSubscriptionCheckout();
        toast.info("Оплата не завершена. Можно повторить из раздела подписки.");
        clearQuery();
        return;
      }

      if (positive || isReturnFlow) {
        const nextState = await loadSubscriptionData({ silent: true });
        await boot().catch(() => {});

        const nextPlanCode = String(nextState?.planCode || "").trim().toUpperCase();
        const nextStatus = String(nextState?.status || "").trim().toUpperCase();
        const nextBillingDate = String(nextState?.nextBillingDate || "").trim();
        const nextBillingMode = String(nextState?.billingMode || "").trim().toLowerCase();

        const prevPlanCode = String(pending?.previousPlanCode || "").trim().toUpperCase();
        const prevStatus = String(pending?.previousStatus || "").trim().toUpperCase();
        const prevBillingDate = String(pending?.previousNextBillingDate || "").trim();
        const prevBillingMode = String(pending?.previousBillingMode || "").trim().toLowerCase();
        const targetPlanCode = String(pending?.targetPlanCode || "").trim().toUpperCase();

        const activeNow = isActiveSubscriptionStatus(nextStatus) || Boolean(nextState?.trialActive);
        const targetMatched = targetPlanCode ? nextPlanCode === targetPlanCode : true;
        const changedMeaningfully =
          nextPlanCode !== prevPlanCode ||
          nextBillingDate !== prevBillingDate ||
          nextBillingMode !== prevBillingMode ||
          (isActiveSubscriptionStatus(nextStatus) && !isActiveSubscriptionStatus(prevStatus));

        if (pending && activeNow && targetMatched && changedMeaningfully) {
          toast.success("Платеж подтвержден. Подписка обновлена.");
          setCheckoutOpen(false);
          clearPendingSubscriptionCheckout();
          clearQuery();
          return;
        }

        toast.info("Оплата не подтверждена. Если платёж уже прошёл, обновите страницу через несколько секунд.");
        clearPendingSubscriptionCheckout();
        clearQuery();
        return;
      }

      clearQuery();
    })();
  }, [boot, isClientRole, loadSubscriptionData, searchParams, setSearchParams, toast]);

  const currentPlan = React.useMemo(
    () => planByCode(plans, subscriptionState?.planCode) || planByCode(plans, "FREE"),
    [plans, subscriptionState?.planCode]
  );

  const checkoutPlan = React.useMemo(() => {
    const chosen = planByCode(plans, checkoutPlanCode);
    if (chosen) return chosen;
    return planByCode(plans, resolveDefaultPlanCode(plans, subscriptionState?.planCode));
  }, [plans, checkoutPlanCode, subscriptionState?.planCode]);

  const trialDays = Number(subscriptionState?.trialDaysLeft) || 0;
  const trialActive = Boolean(subscriptionState?.trialActive && trialDays > 0);

  const topbarBadge = isClientRole
    ? verificationBadgeLabel(verificationState)
    : trialActive
      ? `Пробный период · ${trialDays} ${trialDays === 1 ? "день" : trialDays < 5 ? "дня" : "дней"}`
      : "Подписка активна";

  const topbarBadgeClassName = isClientRole
    ? `subscription-shell__badge is-${verificationStatusTone(verificationState)}`
    : `subscription-shell__badge ${trialActive ? "is-trial" : ""}`.trim();

  const openCheckout = React.useCallback((planCode) => {
    const code = String(planCode || "").trim().toUpperCase();
    if (!code) return;
    setCheckoutPlanCode(code);
    setCheckoutBillingMode(billingMode);
    setCheckoutCodeType("promo");
    setCheckoutCodeValue("");
    setCheckoutCodeError("");
    setCheckoutDiscountPreview(null);
    setCheckoutOpen(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [billingMode]);

  const scrollToPlans = React.useCallback(() => {
    if (!plansSectionRef.current) return;
    plansSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const handleCancelSubscription = React.useCallback(async () => {
    if (!subscriptionState?.canCancel) return;
    const ok = window.confirm("Отключить автопродление подписки?");
    if (!ok) return;

    setCancellingSubscription(true);
    try {
      await subscriptionsApi.cancelSubscription();
      toast.success("Автопродление отключено.");
      await boot().catch(() => {});
      await loadSubscriptionData({ silent: true });
    } catch (err) {
      toast.error(getErrorMessage(err, "Не удалось отменить подписку."));
    } finally {
      setCancellingSubscription(false);
    }
  }, [boot, loadSubscriptionData, subscriptionState?.canCancel, toast]);

  const handleSubmitVerificationRequest = React.useCallback(async () => {
    setSubmittingVerification(true);
    try {
      const nextState = normalizeVerificationState(
        await psychologistAccessApi.submitRequest(verificationNote)
      );
      setVerificationState(nextState);
      setVerificationNote("");
      toast.success("Заявка отправлена модераторам.");
    } catch (err) {
      toast.error(getErrorMessage(err, "Не удалось отправить заявку."));
    } finally {
      setSubmittingVerification(false);
    }
  }, [toast, verificationNote]);

  const handleCreateCheckout = React.useCallback(async () => {
    if (!checkoutPlan) {
      toast.error("Не удалось определить выбранный тариф.");
      return;
    }

    if ((Number(checkoutPlan.monthlyPrice) || 0) <= 0) {
      toast.info("Бесплатный тариф не требует оплаты.");
      setCheckoutOpen(false);
      return;
    }

    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const returnUrl = `${origin}/dashboard/subscription?payment=return&provider=yookassa`;
    const cancelUrl = `${origin}/dashboard/subscription?payment=cancel&provider=yookassa`;
    const normalizedCheckoutCode = String(checkoutCodeValue || "").trim().toUpperCase();
    const checkoutCodePayload = normalizedCheckoutCode
      ? checkoutCodeType === "certificate"
        ? { discountCode: normalizedCheckoutCode, certificateCode: normalizedCheckoutCode }
        : { discountCode: normalizedCheckoutCode, promoCode: normalizedCheckoutCode }
      : {};

    setCreatingPayment(true);
    try {
      const checkout = await subscriptionsApi.createCheckout({
        planCode: checkoutPlan.code,
        billingMode: checkoutBillingMode,
        paymentMethod,
        returnUrl,
        cancelUrl,
        ...checkoutCodePayload,
      });

      const redirectUrl = String(checkout?.confirmationUrl || "").trim();
      if (redirectUrl) {
        writePendingSubscriptionCheckout({
          targetPlanCode: String(checkoutPlan.code || "").trim().toUpperCase(),
          previousPlanCode: String(subscriptionState?.planCode || "").trim().toUpperCase(),
          previousStatus: String(subscriptionState?.status || "").trim().toUpperCase(),
          previousBillingMode: String(subscriptionState?.billingMode || "").trim().toLowerCase(),
          previousNextBillingDate: String(subscriptionState?.nextBillingDate || "").trim(),
          startedAt: new Date().toISOString(),
        });
        if (assignExternalUrl(redirectUrl)) return;

        if (redirectUrl.startsWith("/")) {
          window.location.assign(redirectUrl);
          return;
        }
      }

      const checkoutStatus = String(checkout?.status || "").toUpperCase();
      if (checkoutStatus.includes("PAID") || checkoutStatus.includes("SUCCESS") || checkoutStatus.includes("ACTIVE")) {
        toast.success("Подписка активирована.");
        setCheckoutOpen(false);
        await boot().catch(() => {});
        await loadSubscriptionData({ silent: true });
        clearPendingSubscriptionCheckout();
        return;
      }

      throw new Error("Не удалось получить ссылку для оплаты YooKassa.");
    } catch (err) {
      try {
        // Certificate/full-discount flows can activate subscription server-side
        // even when checkout endpoint returns an error in the final response.
        const reconciledState = await loadSubscriptionData({ silent: true });
        await boot().catch(() => {});

        const targetPlanCode = String(checkoutPlan?.code || "").trim().toUpperCase();
        const nextPlanCode = String(reconciledState?.planCode || "").trim().toUpperCase();
        const nextStatus = String(reconciledState?.status || "").trim().toUpperCase();
        const activated =
          Boolean(reconciledState) &&
          (isActiveSubscriptionStatus(nextStatus) || Boolean(reconciledState?.trialActive)) &&
          (!targetPlanCode || targetPlanCode === nextPlanCode);

        if (activated) {
          toast.success("Подписка активирована.");
          setCheckoutOpen(false);
          clearPendingSubscriptionCheckout();
          return;
        }
      } catch {
        // ignore reconcile errors and fall through to regular toast
      }

      clearPendingSubscriptionCheckout();
      toast.error(getErrorMessage(err, "Ошибка при создании платежа YooKassa."));
    } finally {
      setCreatingPayment(false);
    }
  }, [
    boot,
    checkoutBillingMode,
    checkoutCodeType,
    checkoutCodeValue,
    checkoutPlan,
    loadSubscriptionData,
    paymentMethod,
    subscriptionState?.billingMode,
    subscriptionState?.nextBillingDate,
    subscriptionState?.planCode,
    subscriptionState?.status,
    toast,
  ]);

  const currentPlanPrice = React.useMemo(() => {
    const monthly = Number(subscriptionState?.amountMonthly ?? currentPlan?.monthlyPrice) || 0;
    const yearly = Number(subscriptionState?.amountYearly ?? currentPlan?.yearlyTotal) || monthly * 12;
    return { monthly, yearly };
  }, [currentPlan?.monthlyPrice, currentPlan?.yearlyTotal, subscriptionState?.amountMonthly, subscriptionState?.amountYearly]);

  const checkoutPricing = React.useMemo(
    () => getPlanPrice(checkoutPlan, checkoutBillingMode),
    [checkoutBillingMode, checkoutPlan]
  );

  const checkoutYearSaving = React.useMemo(() => {
    const monthly = Number(checkoutPricing.monthly) || 0;
    const yearlyTotal = Number(checkoutPricing.yearlyTotal) || 0;
    const saving = monthly * 12 - yearlyTotal;
    return saving > 0 ? saving : 0;
  }, [checkoutPricing.monthly, checkoutPricing.yearlyTotal]);

  const normalizedCheckoutCode = React.useMemo(
    () => String(checkoutCodeValue || "").trim().toUpperCase(),
    [checkoutCodeValue]
  );

  React.useEffect(() => {
    if (!checkoutOpen || !checkoutPlan?.code) return undefined;

    if (!normalizedCheckoutCode) {
      setCheckoutCodeError("");
      setCheckoutDiscountPreview(null);
      setCheckingDiscountPreview(false);
      return undefined;
    }

    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const returnUrl = `${origin}/dashboard/subscription?payment=return&provider=yookassa`;
    const cancelUrl = `${origin}/dashboard/subscription?payment=cancel&provider=yookassa`;
    const codePayload = checkoutCodeType === "certificate"
      ? { discountCode: normalizedCheckoutCode, certificateCode: normalizedCheckoutCode }
      : { discountCode: normalizedCheckoutCode, promoCode: normalizedCheckoutCode };

    let cancelled = false;
    const timerId = window.setTimeout(async () => {
      setCheckingDiscountPreview(true);
      try {
        const preview = await subscriptionsApi.previewCheckout({
          planCode: checkoutPlan.code,
          billingMode: checkoutBillingMode,
          paymentMethod,
          returnUrl,
          cancelUrl,
          ...codePayload,
        });
        if (cancelled) return;
        setCheckoutCodeError("");
        setCheckoutDiscountPreview({
          ...preview,
          code: normalizedCheckoutCode,
        });
      } catch (err) {
        if (cancelled) return;
        setCheckoutDiscountPreview(null);
        setCheckoutCodeError(getErrorMessage(err, "Код не применился. Проверьте условия и попробуйте снова."));
      } finally {
        if (!cancelled) setCheckingDiscountPreview(false);
      }
    }, 450);

    return () => {
      cancelled = true;
      window.clearTimeout(timerId);
    };
  }, [checkoutBillingMode, checkoutCodeType, checkoutOpen, checkoutPlan?.code, normalizedCheckoutCode, paymentMethod]);

  const activeDiscountPreview = React.useMemo(() => {
    if (!checkoutDiscountPreview) return null;
    if (!normalizedCheckoutCode || checkoutDiscountPreview.code !== normalizedCheckoutCode) return null;
    return checkoutDiscountPreview;
  }, [checkoutDiscountPreview, normalizedCheckoutCode]);

  const discountAmount = React.useMemo(() => {
    return Number(activeDiscountPreview?.discountAmount) || 0;
  }, [activeDiscountPreview?.discountAmount]);

  const previewBaseAmount = React.useMemo(() => {
    return Number(activeDiscountPreview?.baseAmount) || 0;
  }, [activeDiscountPreview?.baseAmount]);

  const previewFinalAmount = React.useMemo(() => {
    return Number(activeDiscountPreview?.finalAmount) || 0;
  }, [activeDiscountPreview?.finalAmount]);

  const todayCharge = React.useMemo(() => {
    if (trialActive) return 0;
    if (activeDiscountPreview && activeDiscountPreview.discountApplied) {
      return previewFinalAmount;
    }
    if (checkoutBillingMode === BILLING_YEAR) return Number(checkoutPricing.yearlyTotal) || 0;
    return Number(checkoutPricing.monthly) || 0;
  }, [activeDiscountPreview, checkoutBillingMode, checkoutPricing.monthly, checkoutPricing.yearlyTotal, previewFinalAmount, trialActive]);

  const checkoutWillBeDirectActivation = React.useMemo(() => {
    if (trialActive) return true;
    if (activeDiscountPreview && activeDiscountPreview.discountApplied) {
      return previewFinalAmount <= 0;
    }
    return todayCharge <= 0;
  }, [activeDiscountPreview, previewFinalAmount, todayCharge, trialActive]);

  const subtitle = isClientRole
    ? "Кабинет → Заявка в психологи"
    : checkoutOpen
      ? "Подписка → Оплата"
      : "Кабинет → Подписка";

  const verificationTone = verificationStatusTone(verificationState);
  const verificationStatus = String(verificationState?.status || "").trim().toUpperCase();
  const canSubmitVerification = verificationStatus !== "PENDING" && verificationStatus !== "APPROVED";

  return (
    <div className="podcasts-hub subscription-shell">
      <div className="podcasts-shell">
        <AppSidebarV2
          activeKey="subscription"
          mobileOpen={sidebarOpen}
          onCloseMobile={() => setSidebarOpen(false)}
          userName={me?.name || me?.login || "Гость"}
          roleLabel={displayRole(role)}
          canOpenLiteratureEditor={canOpenLiteratureEditor}
        />

        <button
          className={`podcasts-overlay ${sidebarOpen ? "is-open" : ""}`}
          type="button"
          aria-label="Закрыть меню"
          onClick={() => setSidebarOpen(false)}
        />

        <div className="podcasts-main">
          <header className="podcasts-topbar subscription-shell__topbar">
            <div className="podcasts-topbar__left">
              <button
                className="podcasts-topbar__menu-btn"
                type="button"
                onClick={() => setSidebarOpen(true)}
                aria-label="Открыть меню"
              >
                ☰
              </button>

              <div className="subscription-shell__headline">
                <h1 className="subscription-shell__title">
                  {isClientRole ? "Заявка в психологи" : checkoutOpen ? "Оформление подписки" : "Подписка"}
                </h1>
                <p className="subscription-shell__sub">{subtitle}</p>
              </div>
            </div>

            <div className="podcasts-topbar__right">
              <div className={topbarBadgeClassName}>
                <span className="subscription-shell__badge-dot" aria-hidden="true" />
                {topbarBadge}
              </div>

              <button
                className="podcasts-topbar__avatar"
                type="button"
                onClick={() => navigate("/dashboard/settings")}
                title="Открыть настройки"
              >
                {String(me?.name || me?.login || "BS").trim().slice(0, 2).toUpperCase()}
              </button>
            </div>
          </header>

          <main className="subscription-shell__content">
            {loading ? (
              <div className="subscription-panel is-loading">
                {isClientRole ? "Загружаем статус заявки..." : "Загружаем данные подписки..."}
              </div>
            ) : null}

            {!loading && loadError ? (
              <div className="subscription-panel is-error">
                <div className="subscription-panel__title">
                  {isClientRole ? "Не удалось загрузить раздел заявки" : "Не удалось загрузить раздел подписок"}
                </div>
                <p className="subscription-panel__text">{loadError}</p>
                <button
                  type="button"
                  className="btn btn--mint"
                  onClick={() => (isClientRole ? loadVerificationData() : loadSubscriptionData())}
                >
                  Повторить
                </button>
              </div>
            ) : null}

            {!loading && !loadError && isClientRole ? (
              <>
                <section className={`subscription-current-plan subscription-current-plan--application is-${verificationTone}`}>
                  <div className="subscription-current-plan__icon" aria-hidden="true">
                    <PlanShieldIcon />
                  </div>

                  <div className="subscription-current-plan__body">
                    <p className="subscription-current-plan__label">Доступ к роли психолога</p>
                    <h2 className="subscription-current-plan__name">{verificationStatusLabel(verificationState)}</h2>

                    <div className="subscription-current-plan__meta">
                      <span className={`plan-status plan-status--${verificationTone}`}>
                        <i aria-hidden="true" />
                        {verificationBadgeLabel(verificationState)}
                      </span>
                      {verificationState?.requestedAt ? <span>Отправлена {formatDate(verificationState.requestedAt)}</span> : null}
                      {verificationState?.reviewedAt ? <span>Проверена {formatDate(verificationState.reviewedAt)}</span> : null}
                    </div>
                  </div>

                  <div className="subscription-current-plan__actions">
                    {verificationStatus === "APPROVED" ? (
                      <button type="button" className="btn btn--mint" onClick={() => navigate("/dashboard/settings")}>
                        Перейти в кабинет →
                      </button>
                    ) : verificationStatus === "PENDING" ? (
                      <button type="button" className="btn btn--outline" disabled>
                        Заявка уже отправлена
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn btn--mint"
                        onClick={() => window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" })}
                      >
                        Заполнить заявку →
                      </button>
                    )}
                  </div>
                </section>

                <section className="subscription-application-grid">
                  <article className="subscription-application-card">
                    <h2>Что проверяет модерация</h2>
                    <ul className="subscription-application-list">
                      <li>Профиль и контактные данные.</li>
                      <li>Готовность работать как специалист на платформе.</li>
                      <li>Дополнительные комментарии, если они нужны для старта.</li>
                    </ul>
                    <p className="subscription-panel__text">
                      После одобрения заявка переводится в статус психолога, и вам откроются рабочие разделы кабинета.
                    </p>
                  </article>

                  <article className="subscription-application-card">
                    <h2>Статус сейчас</h2>
                    <div className={`subscription-application-status is-${verificationTone}`}>
                      <strong>{verificationStatusLabel(verificationState)}</strong>
                      <span>
                        {verificationStatus === "APPROVED"
                          ? "Доступ подтверждён. Если роль ещё не обновилась в интерфейсе, перезайдите в аккаунт."
                          : verificationStatus === "PENDING"
                            ? "Модераторы получили заявку. Когда решение будет принято, вы увидите уведомление в кабинете."
                            : verificationStatus === "REJECTED"
                              ? "Можно отправить заявку повторно после корректировок."
                              : "Заявка ещё не отправлена."}
                      </span>
                    </div>
                    {verificationState?.reviewNote ? (
                      <div className="subscription-application-note">
                        <strong>Комментарий модератора</strong>
                        <p>{verificationState.reviewNote}</p>
                      </div>
                    ) : null}
                  </article>
                </section>

                <section className="subscription-panel subscription-application-form">
                  <div className="subscription-panel__title">
                    {verificationStatus === "REJECTED" ? "Отправить заявку повторно" : "Заявка в психологи"}
                  </div>
                  <p className="subscription-panel__text">
                    Коротко опишите опыт, направление работы или что важно учесть при рассмотрении. Поле необязательное.
                  </p>

                  <textarea
                    className="subscription-application-form__textarea"
                    value={verificationNote}
                    onChange={(event) => setVerificationNote(event.target.value)}
                    placeholder="Например: работаю в подходе КПТ, хочу вести индивидуальные сессии и публиковать материалы."
                    maxLength={500}
                    disabled={!canSubmitVerification || submittingVerification}
                  />

                  <div className="subscription-application-form__foot">
                    <span>{String(verificationNote || "").length}/500</span>
                    <button
                      type="button"
                      className="btn btn--mint"
                      onClick={handleSubmitVerificationRequest}
                      disabled={!canSubmitVerification || submittingVerification}
                    >
                      {submittingVerification
                        ? "Отправляем..."
                        : verificationStatus === "REJECTED"
                          ? "Отправить повторно"
                          : "Отправить заявку"}
                    </button>
                  </div>
                </section>
              </>
            ) : null}

            {!loading && !loadError && !checkoutOpen ? (
              !isClientRole ? (
              <>
                {trialActive ? (
                  <section className="subscription-trial">
                    <div className="subscription-trial__icon" aria-hidden="true"><SubscriptionIcon name="calendar" strokeWidth={1.8} /></div>
                    <div className="subscription-trial__body">
                      <h2 className="subscription-trial__title">Пробный период активен — доступ открыт ко всем функциям</h2>
                      <p className="subscription-trial__text">
                        Выберите подходящий тариф до {formatDate(subscriptionState?.trialEndsAt)}, чтобы сохранить доступы и данные.
                      </p>
                    </div>
                    <div className="subscription-trial__days">
                      <strong>{trialDays}</strong>
                      <span>дней осталось</span>
                    </div>
                  </section>
                ) : null}

                <section className="subscription-current-plan">
                  <div className="subscription-current-plan__icon" aria-hidden="true">
                    <PlanShieldIcon />
                  </div>

                  <div className="subscription-current-plan__body">
                    <p className="subscription-current-plan__label">Текущий тариф</p>
                    <h2 className="subscription-current-plan__name">{subscriptionState?.planName || currentPlan?.name || "Бесплатный"}</h2>

                    <div className="subscription-current-plan__meta">
                      <span className={`plan-status plan-status--${statusTone(subscriptionState)}`}>
                        <i aria-hidden="true" />
                        {statusLabel(subscriptionState)}
                      </span>

                      {subscriptionState?.trialEndsAt ? (
                        <span>Истекает {formatDate(subscriptionState.trialEndsAt)}</span>
                      ) : null}

                      {!trialActive ? (
                        <span>
                          {subscriptionState?.billingMode === BILLING_YEAR
                            ? `${formatMoney(currentPlanPrice.yearly)}/год`
                            : `${formatMoney(currentPlanPrice.monthly)}/мес`}
                        </span>
                      ) : (
                        <span>{`${formatMoney(currentPlanPrice.monthly)}/мес после trial`}</span>
                      )}
                    </div>
                  </div>

                  <div className="subscription-current-plan__actions">
                    <button
                      type="button"
                      className="btn btn--outline"
                      disabled={!subscriptionState?.canCancel || cancellingSubscription}
                      onClick={handleCancelSubscription}
                    >
                      {cancellingSubscription ? (
                        "Отменяем..."
                      ) : (
                        <>
                          <SubscriptionIcon name="refresh" className="btn__icon" strokeWidth={1.8} />
                          <span>Отключить автопродление</span>
                        </>
                      )}
                    </button>

                    <button type="button" className="btn btn--mint" onClick={scrollToPlans}>
                      Изменить тариф →
                    </button>
                  </div>
                </section>

                <section className="subscription-plans" ref={plansSectionRef}>
                  <div className="subscription-plans__head">
                    <h2>Выберите тариф</h2>

                    <div className="billing-toggle" role="tablist" aria-label="Режим оплаты">
                      <button
                        type="button"
                        className={`billing-toggle__btn ${billingMode === BILLING_MONTH ? "is-active" : ""}`}
                        onClick={() => setBillingMode(BILLING_MONTH)}
                        role="tab"
                        aria-selected={billingMode === BILLING_MONTH}
                      >
                        Помесячно
                      </button>
                      <button
                        type="button"
                        className={`billing-toggle__btn ${billingMode === BILLING_YEAR ? "is-active" : ""}`}
                        onClick={() => setBillingMode(BILLING_YEAR)}
                        role="tab"
                        aria-selected={billingMode === BILLING_YEAR}
                      >
                        Ежегодно <span className="billing-toggle__save">−10%</span>
                      </button>
                    </div>
                  </div>

                  <div className="subscription-plans__grid">
                    {plans.map((plan) => {
                      const isCurrent = String(plan.code || "").toUpperCase() === String(subscriptionState?.planCode || "").toUpperCase();
                      const pricing = getPlanPrice(plan, billingMode);
                      const yearlySummary =
                        Number(pricing.yearlyTotal) > 0 ? `${formatMoney(pricing.yearlyTotal)}/год` : "";
                      const isPaidPlan = (Number(plan?.monthlyPrice) || 0) > 0;
                      const ctaLabel = isCurrent
                        ? "Активный план"
                        : isPaidPlan
                          ? `Выбрать ${plan.name} →`
                          : "Бесплатный тариф";

                      return (
                        <article
                          key={plan.code}
                          className={`subscription-plan-card${isCurrent ? " is-current" : ""}${plan.isPopular ? " is-popular" : ""}`}
                        >
                          {isCurrent ? <div className="subscription-plan-card__badge is-current">Ваш тариф</div> : null}
                          {!isCurrent && plan.isPopular ? (
                            <div className="subscription-plan-card__badge is-popular">Популярный</div>
                          ) : null}

                          <div className="subscription-plan-card__header">
                            <h3>{plan.name}</h3>
                            <p>{plan.description || "Тариф платформы"}</p>
                          </div>

                          <div className="subscription-plan-card__price">
                            <strong>{new Intl.NumberFormat("ru-RU").format(pricing.amount)}</strong>
                            <span>{pricing.period}</span>
                          </div>
                          <div className={`subscription-plan-card__note ${billingMode === BILLING_YEAR ? "is-yearly" : ""}`}>
                            {yearlySummary ? (
                              <>
                                {billingMode === BILLING_MONTH ? (
                                  <span className="subscription-plan-card__note-prefix">или </span>
                                ) : null}
                                <span className="subscription-plan-card__note-value">{yearlySummary}</span>
                              </>
                            ) : (
                              " "
                            )}
                          </div>

                          <ul className="subscription-plan-card__features">
                            {(plan.features || []).map((feature) => (
                              <li key={`${plan.code}-${feature.label}`} className={!feature.enabled ? "is-disabled" : ""}>
                                <i aria-hidden="true"><FeatureStateIcon enabled={feature.enabled} /></i>
                                <span>{feature.label}</span>
                              </li>
                            ))}
                          </ul>

                          <button
                            type="button"
                            className={`btn btn--plan ${isCurrent ? "is-current" : ""}${plan.isPopular ? " is-popular" : ""}`}
                            disabled={isCurrent || !isPaidPlan}
                            onClick={() => openCheckout(plan.code)}
                          >
                            {ctaLabel}
                          </button>
                        </article>
                      );
                    })}
                  </div>
                </section>

                <section className="subscription-why">
                  <h2>Почему стоит перейти на платный тариф</h2>
                  <div className="subscription-why__grid">
                    <article>
                      <div className="icon is-mint"><SubscriptionIcon name="workspace" strokeWidth={1.65} /></div>
                      <h3>Все в одном окне</h3>
                      <p>Клиенты, сессии, чаты, выплаты и заметки в одном кабинете без сторонних сервисов.</p>
                    </article>

                    <article>
                      <div className="icon is-lav"><SubscriptionIcon name="analytics" strokeWidth={1.65} /></div>
                      <h3>Аналитика практики</h3>
                      <p>Динамика дохода, загрузки и возвращаемости клиентов по месяцам.</p>
                    </article>

                    <article>
                      <div className="icon is-grey"><SubscriptionIcon name="scale" strokeWidth={1.65} /></div>
                      <h3>Масштаб без лимитов</h3>
                      <p>Рост практики без искусственных ограничений на клиентов и расписание.</p>
                    </article>
                  </div>

                  <div className="subscription-trust">
                    <span><SubscriptionIcon name="check" strokeWidth={2} /> Отмена в любой момент</span>
                    <span><SubscriptionIcon name="check" strokeWidth={2} /> Оплата через YooKassa</span>
                    <span><SubscriptionIcon name="shield-check" strokeWidth={1.8} /> Данные остаются в кабинете</span>
                    <span><SubscriptionIcon name="check" strokeWidth={2} /> Скидка 10% при оплате за год</span>
                  </div>
                </section>
              </>
              ) : null
            ) : null}

            {!loading && !loadError && checkoutOpen ? (
              <section className="subscription-checkout">
                <div className="subscription-checkout__head">
                  <button type="button" className="btn btn--outline" onClick={() => setCheckoutOpen(false)}>
                    ← Назад к тарифам
                  </button>
                  <div>
                    <h2>Оформление подписки</h2>
                    <p>Платеж пройдет через YooKassa в защищенном окне.</p>
                  </div>
                </div>

                <div className="subscription-checkout__grid">
                  <div className="subscription-checkout__main">
                    <article className="subscription-summary-card">
                      <div className="subscription-summary-card__icon" aria-hidden="true">
                        <PlanShieldIcon />
                      </div>
                      <p className="subscription-summary-card__label">Выбранный тариф</p>
                      <div className="subscription-summary-card__row">
                        <div>
                          <h3>{checkoutPlan?.name || "Тариф"}</h3>
                          <p>{checkoutPlan?.description || "Подписка платформы"}</p>
                        </div>
                        <div className="subscription-summary-card__price">
                          <strong>{formatMoney(checkoutPricing.amount)}</strong>
                          <span>{checkoutPricing.period}</span>
                        </div>
                      </div>
                    </article>

                    <article className="subscription-features-card">
                      <h3>Что входит в тариф</h3>
                      <ul>
                        {(checkoutPlan?.features || []).map((feature) => (
                          <li key={`checkout-feature-${feature.label}`}>
                            <i aria-hidden="true"><FeatureStateIcon enabled={feature.enabled} /></i>
                            <span>{feature.label}</span>
                          </li>
                        ))}
                      </ul>
                    </article>
                  </div>

                  <aside className="subscription-checkout__side">
                    <article className="subscription-order-card">
                      <h3>Итог</h3>

                      <div className="billing-toggle is-compact" role="tablist" aria-label="Период оплаты">
                        <button
                          type="button"
                          className={`billing-toggle__btn ${checkoutBillingMode === BILLING_MONTH ? "is-active" : ""}`}
                          onClick={() => setCheckoutBillingMode(BILLING_MONTH)}
                          role="tab"
                          aria-selected={checkoutBillingMode === BILLING_MONTH}
                        >
                          Помесячно
                        </button>
                        <button
                          type="button"
                          className={`billing-toggle__btn ${checkoutBillingMode === BILLING_YEAR ? "is-active" : ""}`}
                          onClick={() => setCheckoutBillingMode(BILLING_YEAR)}
                          role="tab"
                          aria-selected={checkoutBillingMode === BILLING_YEAR}
                        >
                          Ежегодно
                        </button>
                      </div>

                      <div className="subscription-order-card__rows">
                        <div>
                          <span>
                            {checkoutPlan?.name || "Тариф"} · {checkoutBillingMode === BILLING_YEAR ? "ежегодно" : "помесячно"}
                          </span>
                          <strong>
                            {checkoutBillingMode === BILLING_YEAR
                              ? `${formatMoney(checkoutPricing.yearlyTotal)}/год`
                              : `${formatMoney(checkoutPricing.monthly)}/мес`}
                          </strong>
                        </div>

                        {checkoutBillingMode === BILLING_YEAR && checkoutYearSaving > 0 ? (
                          <div>
                            <span className="is-mint">Скидка за год</span>
                            <strong className="is-mint">−{formatMoney(checkoutYearSaving)}</strong>
                          </div>
                        ) : null}

                        {activeDiscountPreview?.discountApplied ? (
                          <>
                            <div>
                              <span>
                                {activeDiscountPreview.discountCodeType === "CERTIFICATE"
                                  ? "Сертификат"
                                  : "Промокод"} {activeDiscountPreview.discountCode || normalizedCheckoutCode}
                              </span>
                              <strong className="is-mint">−{formatMoney(discountAmount)}</strong>
                            </div>
                            <div>
                              <span>Сумма до скидки</span>
                              <strong>{formatMoney(previewBaseAmount)}</strong>
                            </div>
                          </>
                        ) : null}

                        <div>
                          <span>Пробный период</span>
                          <strong className="is-mint">{trialActive ? `${trialDays} дней` : "Не применяется"}</strong>
                        </div>
                      </div>

                      <div className="subscription-order-card__total">
                        <span>Сегодня к оплате</span>
                        <strong>{formatMoney(todayCharge)}</strong>
                      </div>

                      <p className="subscription-order-card__note">
                        {trialActive
                          ? `Списание после пробного периода: ${checkoutBillingMode === BILLING_YEAR ? `${formatMoney(checkoutPricing.yearlyTotal)}/год` : `${formatMoney(checkoutPricing.monthly)}/мес`}`
                          : "Сумма будет списана сразу после подтверждения оплаты в YooKassa."}
                      </p>
                    </article>

                    <article className="subscription-payment-card">
                      <h3>Способ оплаты</h3>

                      <div className="subscription-payment-card__methods">
                        <label className={paymentMethod === "card" ? "is-active" : ""}>
                          <input
                            type="radio"
                            name="payment-method"
                            value="card"
                            checked={paymentMethod === "card"}
                            onChange={() => setPaymentMethod("card")}
                          />
                          <SubscriptionIcon name="card" className="subscription-payment-card__method-icon" strokeWidth={1.75} />
                          <span>Банковская карта</span>
                        </label>

                        <label className={paymentMethod === "sbp" ? "is-active" : ""}>
                          <input
                            type="radio"
                            name="payment-method"
                            value="sbp"
                            checked={paymentMethod === "sbp"}
                            onChange={() => setPaymentMethod("sbp")}
                          />
                          <SubscriptionIcon name="bank" className="subscription-payment-card__method-icon" strokeWidth={1.65} />
                          <span>СБП</span>
                        </label>
                      </div>

                      <div className="subscription-code-card">
                        <p className="subscription-code-card__title">Промокод или сертификат</p>
                        <div className="subscription-code-card__type">
                          <button
                            type="button"
                            className={checkoutCodeType === "promo" ? "is-active" : ""}
                            onClick={() => setCheckoutCodeType("promo")}
                          >
                            Промокод
                          </button>
                          <button
                            type="button"
                            className={checkoutCodeType === "certificate" ? "is-active" : ""}
                            onClick={() => setCheckoutCodeType("certificate")}
                          >
                            Сертификат
                          </button>
                        </div>

                        <div className="subscription-code-card__input-wrap">
                          <input
                            type="text"
                            value={checkoutCodeValue}
                            onChange={(event) => setCheckoutCodeValue(event.target.value)}
                            placeholder={checkoutCodeType === "certificate" ? "Введите код сертификата" : "Введите промокод"}
                            autoCapitalize="characters"
                            autoComplete="off"
                            spellCheck={false}
                          />
                          {checkoutCodeValue ? (
                            <button
                              type="button"
                              className="subscription-code-card__clear"
                              onClick={() => setCheckoutCodeValue("")}
                              aria-label="Очистить код"
                            >
                              ✕
                            </button>
                          ) : null}
                        </div>
                        {checkingDiscountPreview ? (
                          <p className="subscription-code-card__state">Проверяем код...</p>
                        ) : null}
                        {!checkingDiscountPreview && checkoutCodeError ? (
                          <p className="subscription-code-card__state is-error">{checkoutCodeError}</p>
                        ) : null}
                        {!checkingDiscountPreview && !checkoutCodeError && activeDiscountPreview?.discountApplied ? (
                          <p className="subscription-code-card__state is-success">
                            Применено: скидка {formatMoney(discountAmount)}, к оплате {formatMoney(previewFinalAmount)}.
                          </p>
                        ) : null}
                      </div>

                      <button
                        type="button"
                        className="btn btn--mint btn--checkout"
                        onClick={handleCreateCheckout}
                        disabled={creatingPayment || checkingDiscountPreview || (Boolean(normalizedCheckoutCode) && Boolean(checkoutCodeError))}
                      >
                        {creatingPayment
                          ? "Обрабатываем..."
                          : checkoutWillBeDirectActivation
                            ? "Активировать подписку"
                            : "Перейти к оплате YooKassa →"}
                      </button>

                      <p className="subscription-payment-card__note">
                        <SubscriptionIcon name="lock" strokeWidth={1.7} />
                        <span>
                          {checkoutWillBeDirectActivation
                            ? "Подписка активируется сразу: переход в YooKassa не потребуется."
                            : "Нажатие откроет защищенную страницу YooKassa. После оплаты вы вернетесь в этот раздел."}
                        </span>
                      </p>
                    </article>
                  </aside>
                </div>
              </section>
            ) : null}
          </main>
        </div>
      </div>
    </div>
  );
}
